"use client";

import { useDebounce } from "@/hooks";
import { useAnalytics } from "@/hooks/useAnalytics";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import React, { Suspense, useCallback, useEffect, useState } from "react";
import Search from "./Search";

interface ListingsSearchProps {
  onChange?: (value: string) => void;
  placeholder?: string;
}

const ListingsSearchContent: React.FC<ListingsSearchProps> = ({
  onChange,
  placeholder = "Search",
}) => {
  const [value, setValue] = useState("");
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { trackSearch } = useAnalytics();

  useEffect(() => {
    setValue(searchParams.get("searchQuery") || "");
  }, [searchParams]);

  const updateURL = useCallback((searchValue: string) => {
    const params = new URLSearchParams(searchParams.toString());

    if (searchValue) {
      params.set("searchQuery", searchValue);
    } else {
      params.delete("searchQuery");
    }

    params.delete("page");

    const queryString = params.toString();
    const url = queryString ? `/listings?${queryString}` : '/listings';
    router.push(url);
  }, [searchParams, router]);

  const handleSearch = useCallback((searchValue: string, source: string, debounced: boolean) => {
    const trimmedValue = searchValue.trim();

    // Always navigate to listings with search query, regardless of current page
    if (trimmedValue) {
      updateURL(trimmedValue);
    }

    if (trimmedValue) {
      const currentFilters = Object.fromEntries(searchParams);
      trackSearch(trimmedValue, currentFilters, {
        source,
        page: pathname,
        debounced
      });
    }

    onChange?.(searchValue);
  }, [pathname, onChange, updateURL, trackSearch, searchParams]);

  const handleDebouncedSearch = useCallback((val: string) => {
    handleSearch(val, "search_input", true);
  }, [handleSearch]);

  useDebounce(value, handleDebouncedSearch, 500);

  const handleSearchClick = useCallback(() => {
    handleSearch(value, "search_button", false);
  }, [value, handleSearch]);

  const handleClear = useCallback(() => {
    setValue("");
    onChange?.("");

    if (pathname === "/listings") {
      updateURL("");
    }

    trackSearch("", {}, {
      source: "search_clear",
      page: pathname,
      action: "clear"
    });
  }, [pathname, onChange, updateURL, trackSearch]);

  return (
    <Search
      value={value}
      onChange={setValue}
      onSearch={handleSearchClick}
      onClear={handleClear}
      placeholder={placeholder}
    />
  );
};

const ListingsSearch: React.FC<ListingsSearchProps> = (props) => (
  <Suspense
    fallback={
      <Search
        value=""
        onChange={() => { }}
        placeholder="Loading..."
      />
    }
  >
    <ListingsSearchContent {...props} />
  </Suspense>
);

export default React.memo(ListingsSearch);
